import { useId, type ReactNode } from 'react'

import { AlertIcon } from '@/components/ui/icons'
import { cn } from '@/lib/utils/cn'

import styles from './Field.module.css'

/** What the shell hands its control so the accessibility wiring is automatic. */
export interface FieldControlProps {
  id: string
  'aria-describedby': string | undefined
  'aria-invalid': true | undefined
}

export interface FieldProps {
  label: string
  /** Validation message. Its presence is what marks the field invalid. */
  error?: string
  /** Guidance shown while the field is valid. */
  hint?: string
  /** Marks the field as optional in the label — required is the default. */
  optional?: boolean
  /**
   * Keeps the label for screen readers but takes it out of the layout, for
   * toolbar filters where the control's own value already says what it is.
   */
  hideLabel?: boolean
  id?: string
  className?: string
  children: (control: FieldControlProps) => ReactNode
}

/**
 * The labelled shell every form control shares: label, message, and the id /
 * aria-describedby / aria-invalid wiring, handed to the control as a render
 * prop. Text inputs, selects, textareas and custom controls all sit in this,
 * so a field looks and behaves the same wherever it appears.
 */
export function Field({
  label,
  error,
  hint,
  optional = false,
  hideLabel = false,
  id,
  className,
  children,
}: FieldProps) {
  const generatedId = useId()
  const fieldId = id ?? generatedId
  const messageId = `${fieldId}-message`
  const message = error ?? hint

  return (
    <div className={cn(styles.field, className)}>
      <label className={cn(styles.label, hideLabel && styles.visuallyHidden)} htmlFor={fieldId}>
        {label}
        {optional && <span className={styles.optional}>(optional)</span>}
      </label>

      {children({
        id: fieldId,
        'aria-describedby': message ? messageId : undefined,
        'aria-invalid': error ? true : undefined,
      })}

      {message && (
        <span
          id={messageId}
          className={cn(styles.message, error && styles.error)}
          role={error ? 'alert' : undefined}
        >
          {error && <AlertIcon className={styles.errorIcon} />}
          {message}
        </span>
      )}
    </div>
  )
}
